import { useState } from "react";
import { Menu, Moon, Sun, Search, LogOut, User as UserIcon, History } from "lucide-react";
import { useTheme } from "@/app/providers/ThemeProvider";
import { useSession } from "@/lib/auth/session";
import { PERMISSIONS } from "@/lib/permissions/catalog";
import { cn } from "@/lib/utils/cn";
import { NotificationBell } from "./NotificationBell";

type TopbarProps = {
  onOpenDrawer: () => void;
  showMenuButton: boolean;
  onOpenSearch: () => void;
};

export function Topbar({ onOpenDrawer, showMenuButton, onOpenSearch }: TopbarProps) {
  const { theme, toggle } = useTheme();
  const user = useSession((s) => s.user);
  const logout = useSession((s) => s.logout);
  const hasPermission = useSession((s) => s.hasPermission);
  // El botón "Ver versión anterior" es de uso administrativo crítico
  // (lleva al Nexus 1.0 que tiene endpoints sin migrar). Lo reservamos
  // a Owner — gateado con NEXUS_CONTROL_VIEW que solo Owner tiene.
  // Decisión 29/may/2026.
  const canSeeLegacyToggle = hasPermission(PERMISSIONS.NEXUS_CONTROL_VIEW);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header
      className={cn(
        "sticky top-0 z-20 h-14 flex items-center gap-2 px-3 sm:px-4",
        "border-b border-[var(--nx-topbar-border)]",
        "bg-[var(--nx-topbar-bg)] nx-blur-bg",
      )}
    >
      {showMenuButton && (
        <button
          onClick={onOpenDrawer}
          className="p-2 -ml-1 rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg transition-colors nx-no-tap-highlight"
          aria-label="Abrir menú"
        >
          <Menu size={20} />
        </button>
      )}

      <button
        onClick={onOpenSearch}
        className={cn(
          "flex-1 max-w-md flex items-center gap-2 px-3 h-9 rounded-md",
          "bg-bg-subtle border border-border text-fg-subtle text-sm",
          "hover:bg-bg-hover hover:border-border-strong transition-colors",
        )}
      >
        <Search size={15} strokeWidth={1.75} />
        <span className="flex-1 text-left truncate">Buscar en Nexus…</span>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-2xs font-mono bg-bg-card border border-border text-fg-subtle">
          ⌘K
        </kbd>
      </button>

      <div className="flex-1" />

      <NotificationBell />

      <button
        onClick={toggle}
        className="p-2 rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg transition-colors nx-no-tap-highlight"
        aria-label={theme === "dark" ? "Cambiar a tema claro" : "Cambiar a tema oscuro"}
        title={theme === "dark" ? "Tema claro" : "Tema oscuro"}
      >
        {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* User menu */}
      <div className="relative">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-md hover:bg-bg-hover transition-colors nx-no-tap-highlight"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <Avatar name={user?.name ?? "?"} url={user?.avatarUrl} />
          <span className="hidden sm:inline text-sm font-medium text-fg max-w-[140px] truncate">
            {user?.name ?? "Invitado"}
          </span>
        </button>

        {menuOpen && (
          <>
            <div
              className="fixed inset-0 z-30"
              onClick={() => setMenuOpen(false)}
              aria-hidden
            />
            <div className="absolute right-0 top-full mt-1 w-56 z-40 bg-bg-elevated border border-border rounded-lg shadow-nx-lg overflow-hidden animate-fade-in">
              <div className="px-3 py-2.5 border-b border-border">
                <div className="text-sm font-medium text-fg truncate">{user?.name}</div>
                <div className="text-xs text-fg-subtle truncate">{user?.email}</div>
                {user?.roles && user.roles.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {user.roles.map((r) => (
                      <span key={r.slug} className="nx-badge-accent">
                        {r.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="py-1">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    window.location.href = "/control/users/me";
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-fg hover:bg-bg-hover transition-colors"
                >
                  <UserIcon size={15} /> Mi perfil
                </button>
                {/* Acceso al back-office legacy mientras dure la migración.
                    Pasa el JWT actual como nexusToken para que la landing
                    HTML haga SSO sin pedir login de nuevo. Se quitará
                    cuando todos los módulos legacy estén migrados.
                    Gateado a Owner (NEXUS_CONTROL_VIEW). El Nexus 1.0
                    tiene módulos sin gating fino completamente migrado,
                    así que solo Owner debería poder accederlo. */}
                {canSeeLegacyToggle && (
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      const token = (() => {
                        try {
                          return localStorage.getItem("nx_token") || "";
                        } catch {
                          return "";
                        }
                      })();
                      const url = token
                        ? `/nexus-landing.html?nexusToken=${encodeURIComponent(token)}`
                        : "/nexus-landing.html";
                      window.location.href = url;
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-fg hover:bg-bg-hover transition-colors"
                    title="Versión anterior del back-office (Matrix legacy)"
                  >
                    <History size={15} /> Ver versión anterior
                  </button>
                )}
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-danger-soft transition-colors"
                >
                  <LogOut size={15} /> Cerrar sesión
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
}

function Avatar({ name, url }: { name: string; url?: string | null }) {
  const initial = name?.[0]?.toUpperCase() ?? "?";
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className="w-7 h-7 rounded-full object-cover border border-border"
      />
    );
  }
  return (
    <div className="w-7 h-7 rounded-full bg-accent-soft text-accent text-xs font-bold flex items-center justify-center">
      {initial}
    </div>
  );
}
